import api from "./api";

let authCookie = null; // Store the auth cookie

export const mapRole = {
  A: "Super Admin",
  CA: "Public",
  GL: "Public",
  C: "Collector",
  POCA: "POC Attendant",
  U: "Public",
};

export const mapUserStatus = {
  A: "Active",
  P: "Pending",
  S: "Suspended",
  D: "Deactivated",
};

// TODO: Change mapUserStatus into a function that will handle "Invitation sent" status


export const userDataMap = (item) => {
  console.log("Raw item data:", item); // Debug log to see the complete item structure
  return {
    userId: item?._id ?? null,
    userName: item?.name ?? null,
    userFirstName: item?.first_name ?? null,
    userLastName: item?.last_name ?? null,
    userEmail: item?.email ?? item?.authentication_string ?? null,
    verified: item?.approved ?? null,
    primaryProfileId: item?.primary_profile_id?._id ?? null,
    profilePicture: item?.medias?.[0]?.media_url ?? null,
    role: mapRole[item?.role] ?? null,
    status: item?.status ?
      (item?.status === "D" && !item?.approved) ? "Invitation sent"
        : mapUserStatus[item?.status] !== "undefined" ? mapUserStatus[item?.status]
          : null
      : null,
    createDate: item?.create_date ?? null,
    lastLogin: item?.last_login ?? null,
  };
};

export const userService = {
  roleToString: (role) => {
    return mapRole[role] || "Unknown";
  },

  roleToCode: (role) => {
    return role === "Public"
      ? "U"
      : Object.keys(mapRole).find((key) => mapRole[key] === role) || null;
  },

  statusToString: (status) => {
    return mapUserStatus[status] || "Unknown";
  },

  login: async (email, password) => {
    const response = await api.post("/mapi/authentication/login", {
      AuthenticationString: email,
      Password: password,
    });

    if (!response.data) throw new Error("Invalid API response (no data)");

    // console.log("Response.data: ", response.data);

    // Capture the cookie from response headers
    const setCookieHeader = response.headers["Set-Cookie"];

    if (setCookieHeader) {
      console.log("Auth Cookie received 2:", setCookieHeader); // Log the cookie
      authCookie = setCookieHeader;
      api.defaults.headers.Cookie = authCookie; // Set it globally for future requests
      console.log("Auth Cookie received 3:", authCookie);
    }

    return response.data;
  },

  logout: async () => {
    const response = await api.post("/mapi/authentication/logout");

    // Clear the cookie on logout
    authCookie = null;
    delete api.defaults.headers.Cookie;

    return response.data;
  },

  forgotPasswordRequest: async (email) => {
    const response = await api.post(
      "/mapi/authentication/forgotPasswordRequest",
      {
        AuthenticationString: email,
      }
    );

    if (!response.data) throw new Error("Invalid API response (no data)");
    return response.data;
  },

  resetPassword: async ({ EntityId, OldPassword, NewPassword }) => {
    try {
      const response = await api.post("/mapi/authentication/resetPassword", {
        EntityId,
        OldPassword,
        NewPassword,
      });
      return response.data;
    } catch (error) {
      console.error(
        "Error resetting password:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  getUsers: async () => {
    try {
      console.log("Calling get users api");

      const response = await api.get(`/mapi/entity/getEntity`);

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching user list data:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  getUserDetails: async (entityId) => {
    try {
      console.log(`Calling get user details API for EntityId: ${entityId}`);

      const response = await api.get(
        `/mapi/entity/getEntityDetail?EntityId=${entityId}`
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error fetching user details:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  editUserDetails: async (userData) => {
    try {
      console.log("Editing User:", userData);

      const response = await api.post(
        `/mapi/entity/editEntityDetail`,
        userData
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error updating user details:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  bulkUploadUsers: async (userData) => {
    try {
      const response = await api.post(
        "/mapi/authentication/bulkUploadUsers",
        userData // Send JSON directly
      );

      return response.data;
    } catch (error) {
      console.error(
        "Error uploading users:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  getProfile: async () => {
    try {
      const response = await api.get("/mapi/profile/getProfile");
      return response.data?.Data || []; // Return only the profiles array
    } catch (error) {
      console.error("Failed to fetch profiles:", error);
      return [];
    }
  },

  enableUser: async (entityId) => {
    try {
      const response = await api.post("/mapi/authentication/enableUser", {
        EntityId: entityId, // expects string here
      });
      return response.data;
    } catch (error) {
      console.error(
        "Error enabling user:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  suspendUser: async (entityId, comment = "") => {
    try {
      const payload = {
        EntityId: entityId,
      };

      // Only add Comment if it is not empty
      if (comment.trim() !== "") {
        payload.Comment = comment;
      }

      const response = await api.post(
        "/mapi/authentication/suspendUser",
        payload
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error suspending user:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  disableUser: async (entityId) => {
    try {
      const response = await api.post("/mapi/authentication/disableUser", {
        EntityId: entityId, // expects string here
      });
      return response.data;
    } catch (error) {
      console.error(
        "Error disabling user:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  resetEntityRole: async ({ EntityId, Role }) => {
    try {
      const response = await api.post("/mapi/authentication/resetEntityRole", {
        EntityId,
        Role,
      });
      return response.data;
    } catch (error) {
      console.error(
        "Error resetting user role:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  resetPasswordByToken: async ({ newPassword, entityId, token }) => {
    const response = await api.post(
      "/mapi/authentication/resetPasswordByToken",
      {
        NewPassword: newPassword,
        EntityId: entityId,
        ResetPasswordToken: token,
      }
    );
    return response.data;
  },
};

export default userService;
